import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { v4 as uuid } from "uuid";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate } from "react-router-dom";
import { GridColDef, DataGrid } from "@mui/x-data-grid";
import {
  BtnGroupStyle,
  ContentStyle,
  DataGridListStyle,
  LetterSearchStyle,
  SearchBox,
} from "../../Theme";
import { YSButton } from "../../uikit/YSButton";
import { DialogPopup } from "../../components/DialogPopup";
import ConfirmPopup from "../../components/ConfirmPopup";
import { HelpGuideTooltip } from "../../components/HelpGuideTooltip";
import SearchIcon from "../../images/icons/SearchIcon.svg";
import { TextNTooltip, TextOnly } from "../../components/Content";
import useFetch from "../../CustomHooks/useFetch";
import {
  ConfirmPracticeFavDrugsPresc,
  PracticeFavDrugsAlphaColors,
  PracticeFavDrugsList,
  RemovePracticeFavDrugsListById,
} from "../../Apis/AuthApiServices";
import useDebounce from "../../CustomHooks/useDebounce";
import NoRecords from "../../components/NoRecords";
import ErrorOverlay from "../../components/ErrorOverlay";
import YSSnackBar from "../../uikit/YSSnackBar";
import { useSelector } from "react-redux";
import { useOnBehalfContext } from "../../context/OnbehalfContext";

export default function PracticeFavoriteDrugs() {
  const UserServiceState = useSelector((state: any) => {
    return { ...state?.UserServicesSlice?.ServicesData };
  }); //redux data
  const { onBehalfContext }: any = useOnBehalfContext();

  const [deleteFavDrugsPopup, setDeleteFavDrugsPopup] = React.useState(false);
  const [sortByAlpha, setSortByAlpha] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [drugNameInput, setDrugNameInput] = useState("");
  const [selectionData, setSelectionData] = useState("");
  const [deleteStateData, setDeleteStateData] = useState<any>({
    message: "",
    open: false,
    variant: "",
  });

  const columnsPracticeFavoriteDrugs: GridColDef[] = [
    {
      field: "description",
      headerName: "Drug Description",
      cellClassName: "w20",
      headerClassName: "w20",
      width: 170,
      renderCell: ({ row }) => (
        <TextNTooltip
          Text={row.description}
          color={"primary"}
          onClick={() => {
            navigate("/newrxpad", {
              state: {
                rowData: row,
                key: "PracDrugPop",
              },
            });
          }}
        />
      ),
    },
    {
      field: "sig",
      headerName: "SIG",
      cellClassName: "w20",
      headerClassName: "w20",
      width: 170,
      renderCell: (params: any) => <TextNTooltip Text={params.value} />,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      cellClassName: "w10",
      headerClassName: "w10",
      width: 80,
      renderCell: (params: any) => <TextOnly Text={params.value} />,
    },
    {
      field: "refills",
      headerName: "Refills",
      cellClassName: "w10",
      headerClassName: "w10",
      width: 65,
      renderCell: (params: any) => <TextOnly Text={params.value} />,
    },
    {
      field: "rxOtc",
      headerName: "Rx/OTC",
      cellClassName: "w10",
      headerClassName: "w10",
      width: 70,
      renderCell: (params: any) => <TextOnly Text={params.value} />,
    },
    {
      field: "formularyStatus",
      headerName: "Fmlry. Status",
      width: 120,
      cellClassName: "w15",
      headerClassName: "w15",
    },
    {
      field: "copay",
      headerName: "Copay",
      width: 130,
      cellClassName: "w15",
      headerClassName: "w15",
    },
  ];

  const deleteRowData = async () => {
    try {
      const params = new URLSearchParams();
      params.append("selectedPersonDrugIds", selectionData);
      const result = await RemovePracticeFavDrugsListById(params);
      if (result?.data?.digitalRxStatusCode === 200) {
        setDeleteStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.response[0]?.message,
            variant: "success",
            open: true,
          };
        });
        fetchApi();
      } else {
        setDeleteStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
      }
    } catch (err: any) {
      setDeleteStateData((prevState: any) => {
        return {
          ...prevState,
          message: "Try Again After Some Time!",
          variant: "error",
          open: true,
        };
      });
    }
  };

  const debouncedValue = useDebounce(drugNameInput, 1000);

  const doctorId = localStorage.getItem("doctorId");
  const patientId = sessionStorage.getItem("patientId");
  const Params = {
    doctorId: doctorId,
    patientId: patientId,
    pageNumber: page,
    drugName: debouncedValue,
    selAlpha: sortByAlpha,
  };
  const { data, loading, error, fetchApi }: any = useFetch(
    Params,
    PracticeFavDrugsList,
    [page, sortByAlpha, debouncedValue]
  );

  const [rowCountState, setRowCountState] = useState(
    data[0]?.pagination?.totalItems || 0
  );
  const handleShowDeletePopupClick = () => {
    setDeleteFavDrugsPopup(true);
  };
  const handleHideDeletePopupClick = () => {
    setDeleteFavDrugsPopup(false);
  };

  const navigate = useNavigate();
  const practiceFavDrugParams = {
    doctorId: doctorId,
  };
  const { data: PracticeAlphaColors }: any = useFetch(
    practiceFavDrugParams,
    PracticeFavDrugsAlphaColors,
    []
  );
  const prepareAlphabets = () => {
    let result = [];

    for (let i = 65; i < 91; i++) {
      result.push(
        <button
          className={String.fromCharCode(i) === sortByAlpha ? "active" : ""}
          disabled={
            PracticeAlphaColors[0]?.alphabetMap?.includes(
              String.fromCharCode(i)
            )
              ? false
              : true
          }
          type="button"
          key={i}
          onClick={(e: any) => {
            setSortByAlpha(e.target.value);
          }}
          value={String.fromCharCode(i)}
        >
          {String.fromCharCode(i)}
        </button>
      );
    }
    result.push(
      <button
        className={sortByAlpha === "" ? "active" : ""}
        type="button"
        onClick={() => {
          setSortByAlpha("");
        }}
        value={""}
      >
        All
      </button>
    );
    return result;
  };

  useEffect(() => {
    setRowCountState((prevRowCountState: any) =>
      data[0]?.pagination?.totalItems !== undefined
        ? data[0]?.pagination?.totalItems
        : prevRowCountState
    );
  }, [data[0]?.pagination?.totalItems, setRowCountState]);

  const [load, setLoad] = useState(false);
  const confirmPracticeFavDrugPrec = async () => {
    try {
      setLoad(true);
      const bodyData = {
        patientId: patientId,
        userDetails: {
          doctorId:
            UserServiceState?.profileGroup > 6
              ? localStorage.getItem("SupervisorDoctorId")
              : localStorage.getItem("doctorId"),
          onBehalfDoctorId: localStorage.getItem("onBehalf") ?? "",
          selectedClinicId: sessionStorage.getItem("clinicId"),
        },
        prescriptionIds: selectionData === "" ? null : selectionData,
      };
      const result: any = await ConfirmPracticeFavDrugsPresc(bodyData);
      if (result?.data?.digitalRxStatusCode === 200) {
        navigate("/newrxpad/summary", { state: "confirmPfdList" });

        sessionStorage.setItem(
          "locationData",
          JSON.stringify(result?.data?.response[0])
        );
        sessionStorage.setItem(
          "rxTabs",
          JSON.stringify(result?.data?.response[0]?.prescriptionFormList)
        );
        sessionStorage.setItem(
          "rxTabsErrors",
          JSON.stringify(result?.data?.response[0]?.prescriptionErrors)
        );
        setLoad(false);
      } else {
        setDeleteStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
        setLoad(false);
      }
    } catch (er) {
      setDeleteStateData((prevState: any) => {
        return {
          ...prevState,
          message: "Try Again Later!",
          variant: "error",
          open: true,
        };
      });
      setLoad(false);
    }
  };
  if (load)
    return (
      <Grid
        container
        justifyContent={"center"}
        display={"flex"}
        alignItems="center"
        height={"100vh"}
      >
        {" "}
        <CircularProgress color="primary" />
      </Grid>
    );
  return (
    <>
      <ContentStyle>
        <Typography
          className="help"
          component="h6"
          color="primary"
          variant="h6"
        >
          Practice Favorite Drugs{" "}
          <HelpGuideTooltip
            content={
              <>
                {" "}
                <Typography component="div" variant="body1">
                  These are practice level favorite drugs that the prescriber
                  has chosen
                </Typography>
              </>
            }
          />
        </Typography>
        <LetterSearchStyle>{prepareAlphabets()}</LetterSearchStyle>
        <Grid
          container
          item
          xs={12}
          spacing={1}
          alignItems="top"
          justifyContent="flex-start"
          marginBottom={1}
        >
          <Grid item xs={12} md={7} textAlign={"left"}>
            <SearchBox className="searchicon">
              <Typography component="label" color="primary" variant="body2">
                Search by
              </Typography>{" "}
              <TextField
                id="drugname"
                name="DrugName"
                variant="outlined"
                placeholder="Enter Drug name"
                onChange={(e) => {
                  setDrugNameInput(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <img src={SearchIcon} alt="Search" loading="lazy" />
                    </IconButton>
                  ),
                }}
              />{" "}
            </SearchBox>
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          spacing={1}
          alignItems="top"
          justifyContent="center"
        >
          <Grid item xs={12}>
            <DataGridListStyle>
              <DataGrid
                rows={data[0]?.items ?? []}
                headerHeight={36}
                paginationMode="server"
                sortingOrder={["desc", "asc"]}
                columns={columnsPracticeFavoriteDrugs}
                autoHeight
                rowHeight={40}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
                checkboxSelection
                getRowId={(row) => row.personDrugId ?? uuid()}
                loading={loading}
                components={{
                  NoRowsOverlay: NoRecords,
                  NoResultsOverlay: NoRecords,
                  ErrorOverlay: ErrorOverlay,
                }}
                error={error || undefined}
                page={page}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                onPageChange={(newPage) => {
                  setPage(newPage);
                }}
                rowCount={rowCountState}
                onSelectionModelChange={(ids: any) => {
                  setSelectionData(ids);
                }}
                getRowClassName={(params) => {
                  if (params.row.obseletRx) {
                    return "OP";
                  }
                  return "";
                }}
              />
            </DataGridListStyle>
          </Grid>

          <Grid item xs={12}>
            <BtnGroupStyle className="middle">
              <YSButton
                onClick={() => {
                  navigate("/newrxpad");
                }}
                className=""
                children={"Back to NewRx"}
                size={"large"}
                type="button"
                color="primary"
                variant="outlined"
              />{" "}
              <YSButton
                onClick={handleShowDeletePopupClick}
                className=""
                children={"Delete"}
                size={"large"}
                type="button"
                color="primary"
                variant="outlined"
                disabled={
                  // localStorage.getItem("onBehalf")
                  onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                    ? false
                    : UserServiceState?.profileGroup > 6
                    ? true
                    : false
                }
                startIcon={
                  // localStorage.getItem("onBehalf")
                  onBehalfContext?.id && UserServiceState?.profileGroup !== 11
                    ? null
                    : UserServiceState?.profileGroup > 6 && <LockIcon />
                }
              />{" "}
              <YSButton
                onClick={() => {
                  confirmPracticeFavDrugPrec();
                }}
                className=""
                children={"Confirm Favorite Drug(s)"}
                size={"large"}
                type="button"
                color="primary"
                variant="contained"
              />{" "}
            </BtnGroupStyle>
          </Grid>
        </Grid>{" "}
        <YSSnackBar
          message={deleteStateData.message}
          variant={deleteStateData.variant}
          open={deleteStateData.open}
          handleClose={() => {
            setDeleteStateData((prevState: any) => {
              return { ...prevState, open: false };
            });
          }}
        />
      </ContentStyle>
      <DialogPopup
        windowsize={"sm"}
        show={deleteFavDrugsPopup}
        handleShowPopupClick={handleShowDeletePopupClick}
        handleHidePopupClick={handleHideDeletePopupClick}
        close={true}
      >
        <ConfirmPopup
          deleteRowData={deleteRowData}
          handleHideDeletePopupClick={handleHideDeletePopupClick}
        />
      </DialogPopup>
    </>
  );
}
